// THE MAIN THREAD — the assistant's conversation as a VIEW of the main area
// (AI_ASSISTANT_SPEC v1 §1 + §8, v1.1 §12.2; Nika's pick 6 A "reading column +
// wide blocks", the DEFAULT placement — everywhere since 2026-09-29).
//
// It is the SAME thread the sidebar shows — the general-scope conversation
// (`root.chatMessages`), rendered by the shared chat's own row (ChatMessage,
// reused by bare key) — laid over the main area like the generated board: on
// the HOME over the grid area (pages/main.js mounts it as the grid's last
// child; the cards step under the view — inert, faded, functions/
// assistantBoard.js `assistantBoardOpen`), on EVERY OTHER route over the page
// sheet (components/AssistantMainLayer.js mounts the same view; the page stays
// mounted underneath). Nothing on the page moves (Rule 66: closed keeps the
// box, only opacity and visibility change). Text reads in a column
// (`maxWidth: 'I'`, about 750 px) centred on the area; the WIDE blocks — an
// answer view, a records block, a board, an app preview — take the full width
// in their own row (functions/assistantMainThread.js `assistantThreadRows`).
// Follow-ups append below. An opener with nothing in the conversation yet
// shows the empty thread's one-line hint above the composer. "Open in
// sidebar" moves the SAME thread to the docked panel.
//
// Every value comes from functions/assistantMainThread.js through `el.call`;
// the component holds no logic of its own.
export const AssistantMainThread = {
  tag: 'section',
  role: 'region',
  ariaLabel: (el) => el.call('polyglot', 'workspace.assistantThread.region'),
  // The shared chat rows resolve their thread by walking up for `chatScope`.
  chatScope: 'general',

  dataOpen: (el, s) => (el.call('assistantMainThreadOpen', s.root) ? 'true' : 'false'),
  ariaHidden: (el, s) => (el.call('assistantMainThreadOpen', s.root) ? null : 'true'),
  ariaBusy: (el, s) => (s.root && s.root.chatTyping ? 'true' : null),

  display: 'flex',
  flexDirection: 'column',
  gap: 'B',
  // ONE column with the board: the head and the rows sit on the home grid's
  // own width (`--home-grid-w`, pages/main.js _injectWidgetStyles), centred.
  width: '100%',
  maxWidth: 'var(--home-grid-w, 100%)',
  marginInline: 'auto',
  minWidth: '0',
  boxSizing: 'border-box',
  // CLEAR OF THE COMPOSER (Nika 2026-09-30: "give bottom padding so it can
  // have spacing and canvas promptarea does not overlap"). On the home the
  // thread lies over the grid area and scrolls with the page column down to
  // the viewport bottom, where the composer floats: the bottom inset is the
  // composer's LIVE clearance (`--prompt-clear`, the shell's ONE measurement —
  // a ResizeObserver on CanvasPromptTextarea, app.js: autoGrow, the expanded
  // floor, multi-line drafts and the minimize all move it), the phone chip
  // row above it (`--chip-row`, 0 off phones) and one row gap, so at the end
  // of the scroll the last message stands one gap above the composer at any
  // composer height. Over a page the layer's band already ends above the
  // composer (components/AssistantMainLayer.js), which zeroes this there.
  paddingBottom: 'calc(var(--prompt-clear) + var(--chip-row) + var(--spacing-B))',
  // The crossfade (the board's recipe): `C`, opacity only; closed keeps the
  // box and hides after the fade, open shows at once and fades in.
  opacity: '0',
  visibility: 'hidden',
  pointerEvents: 'none',
  transition: 'opacity C defaultBezier, visibility 0s linear C',
  '&[data-open="true"]': {
    opacity: '1',
    visibility: 'visible',
    pointerEvents: 'auto',
    transition: 'opacity C defaultBezier, visibility 0s linear 0s'
  },

  // The flag can only be up with the main placement, on the route the thread
  // opened on: a placement change or a navigation clears it
  // (assistantMainThreadSync), the home going away clears it on the way out.
  // Esc joins the shell keyboard registry while the view is on the page.
  // SPLIT VIEW: a page sheet that opens over the thread on a wide screen
  // moves the conversation beside it (functions/assistantAskRoute.js
  // assistantThreadBesidePane) — never under it, closed by the next ask.
  subscribeTo: [
    'assistantDock',
    'currentPath',
    '_mainThreadPath',
    '_canvasSlot',
    '_canvasSlotSecond'
  ],
  onMounted: (el) => el.call('assistantMainThreadShortcuts', el),
  onUpdate: (el, s) => {
    el.call('assistantMainThreadSync', s.root)
    el.call('assistantThreadBesidePane', s.root)
  },
  // A NEW QUESTION comes to the top of the view, once (Nika 2026-10-23: "when
  // answering in new section always scroll top of that section"): its row
  // lands under the header and the answer reads down from it — never a chase
  // of the stream's bottom (functions/assistantReveal.js). The question is
  // the thread's last row, so the view takes the room it needs below it (its
  // `minHeight` here) and keeps it while the answer fills it in. Only a
  // question ASKED now counts — the count grows with a turn starting (the
  // send writes both at once); opening a conversation moves nothing, and
  // another conversation, a new chat or a deleted one gives the room back.
  minHeight: (el, s) =>
    el.call('assistantRevealRoom', el, s.root, el.call('assistantMainThreadOpen', s.root)),
  stateDeps: [
    (el, s) => (el.call('assistantThreadAsks', s.root) || {}).count || 0,
    (el, s) => (el.call('assistantThreadAsks', s.root) || {}).first || null
  ],
  onStateUpdate: (el, s, ctx, change) => {
    const prev = (change && change.prev) || []
    const next = (change && change.next) || []
    const asked = (Number(next[0]) || 0) > (Number(prev[0]) || 0) && !!(s.root && s.root.chatTyping)
    if (asked && el.call('assistantMainThreadOpen', s.root)) {
      const ask = el.call('assistantThreadAsks', s.root) || {}
      if (ask.key) el.call('assistantRevealTop', el, ask.key, 'ask:' + ask.key)
      return
    }
    if (prev[0] !== next[0] || prev[1] !== next[1]) el.call('assistantRevealRoomClear', el)
  },
  onBeforeRemove: (el, s) => {
    if (typeof el.__threadShortcutsOff === 'function') el.__threadShortcutsOff()
    const root = s && s.root
    if (root && root._homeThreadOpen === true && typeof root.update === 'function') {
      root.update({ _homeThreadOpen: false, _mainThreadPath: null }, { preventFetch: true })
    }
  },

  ThreadHead: {
    flow: 'x',
    flexWrap: 'wrap',
    align: 'center space-between',
    gap: 'Z',
    minWidth: '0',
    // ON THE CONVERSATION'S COLUMN: the reading column's width and centring
    // (ThreadReading below). Across the whole grid the label stood 561px left
    // of the first message on a wide view (measured live 2026-10-18).
    width: '100%',
    maxWidth: 'I',
    marginInline: 'auto',

    AppMicroLabel: {
      dataThreadMicro: true,
      whiteSpace: 'normal',
      text: (el, s) => el.call('assistantThreadMicro', s.root)
    },

    ThreadActions: {
      flow: 'x',
      flexWrap: 'wrap',
      align: 'center',
      gap: 'Y',
      // Ends on the column's end where the bubbles end — also when a phone
      // wraps it under the label (design-direction §3: a lone action in a
      // space-between row takes `marginLeft: 'auto'`).
      marginLeft: 'auto',

      AppCapsuleBtnToolbar: {
        compact: true,
        dataThreadToSidebar: true,
        text: '{{ workspace.assistantThread.openInSidebar | polyglot }}',
        onClick: (e, el) => el.call('assistantThreadToSidebar')
      },

      // The ONE square button of the head: back to the home board, or to the
      // page under the thread. Its name is this view's verb: only the brand's
      // own `attr` key replaces the brand default (the board view's measured
      // note).
      CloseButton: {
        dataThreadClose: true,
        attr: {
          'aria-label': (el, s) =>
            el.call(
              'polyglot',
              el.call('assistantMainHost', s && s.root) === 'over'
                ? 'workspace.assistantThread.closeToPage'
                : 'workspace.assistantThread.close'
            )
        },
        onClick: (e, el) => el.call('assistantMainThreadClose', { focusComposer: true })
      }
    }
  },

  // A link to a conversation this person cannot read: the view says so
  // (components/AssistantThreadUnavailable.js; the thread URL, `?ai=<id>`).
  AssistantThreadUnavailable: {},

  // CHAT'S START (the home's Chat tab, Nika 2026-10-23: "draw related
  // experience for each tab aligning AI mode"): the empty conversation
  // offers the workspace's own starters (components/HomeTabStart.js, tab
  // 'chat'); the one line below still points at the composer.
  HomeTabStart_chat: { tab: 'chat' },

  // The empty thread (an opener in Page mode with nothing asked yet): one
  // line that points at the composer below — never a made-up question.
  ThreadEmpty: {
    tag: 'p',
    dataThreadEmpty: true,
    hide: (el, s) => {
      const list = s && s.root && s.root.chatMessages
      return Array.isArray(list) && list.length > 0
    },
    margin: '0',
    maxWidth: 'I',
    marginInline: 'auto',
    fontSize: 'A',
    color: 'caption',
    textAlign: 'center',
    text: '{{ workspace.assistantThread.emptyHint | polyglot }}'
  },

  ThreadRows: {
    scope: { threadRows: [] },
    dataThreadRows: true,
    flow: 'y',
    gap: 'B',
    minWidth: '0',
    // Declared (FA515): the rows re-derive on every thread write — a send, a
    // streamed delta, a card attached.
    childrenDeps: ['chatMessages'],
    children: (el, s) => el.call('assistantThreadRows', s.root.chatMessages, el.scope),
    childrenAs: 'state',
    childExtends: 'AssistantThreadRow'
  }
}

// One message of the main thread: the shared chat row in the reading column,
// then its wide blocks across the board. Guards `s` — a childExtends template
// evaluates once unbound.
export const AssistantThreadRow = {
  tag: 'article',
  dataThreadRow: (el, s) => (s && s.key) || null,
  dataRole: (el, s) => (s && s.role) || null,
  flow: 'y',
  gap: 'Z',
  width: '100%',
  minWidth: '0',
  // brought to the top as a new question (AssistantMainThread's reveal), it
  // stays clear of the shell's floating header — the Fly page's own margin
  scrollMarginTop: 'calc(var(--shell-top) + var(--spacing-A))',

  ThreadReading: {
    width: '100%',
    maxWidth: 'I',
    marginInline: 'auto',
    minWidth: '0',
    // the first workspace thread's plan, above the summary it rides with
    // (functions/workspaceSetup.js): no card on any other row
    WorkspaceSetupPlanSlot: { planOf: 'row', marginBottom: 'Z' },
    ChatMessage: {}
  },

  ThreadWide: {
    dataThreadWide: true,
    hide: (el, s) => !(s && Array.isArray(s._wide) && s._wide.length),
    flow: 'y',
    gap: 'Z',
    width: '100%',
    minWidth: '0',
    childExtends: 'ChatCard',
    childrenAs: 'state',
    children: (el, s) => (s && Array.isArray(s._wide) ? s._wide : [])
  }
}
